import { useCallback, useContext } from 'react';
import { useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';

import {
  ChainlitContext,
  IStarter,
  IStep,
  useAuth,
  useChatData,
  useChatInteract
} from '@chainlit/react-client';

import { Button } from '@/components/ui/button';

import { persistentCommandState } from '@/state/chat';

interface StarterProps {
  starter: IStarter;
}

export default function Starter({ starter }: StarterProps) {
  const apiClient = useContext(ChainlitContext);
  const selectedCommand = useRecoilValue(persistentCommandState);
  const { sendMessage } = useChatInteract();
  const { loading, connected } = useChatData();
  const { user } = useAuth();

  const disabled = loading || !connected;

  const onSubmit = useCallback(async () => {
    const message: IStep = {
      threadId: '',
      id: uuidv4(),
      command: starter.command ?? selectedCommand?.id,
      name: user?.identifier || 'User',
      type: 'user_message',
      output: starter.message,
      createdAt: new Date().toISOString(),
      metadata: { location: window.location.href }
    };

    sendMessage(message, []);
  }, [user, selectedCommand, sendMessage, starter]);

  return (
    <Button
      id={`starter-${starter.label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')}`}
      variant="outline"
      className="group h-auto min-h-10 w-full justify-start rounded-[10px] border-primary/15 bg-primary/5 px-3.5 py-2.5 text-left shadow-none transition-[background-color,border-color,color] duration-150 hover:border-primary/30 hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/20"
      disabled={disabled}
      onClick={onSubmit}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        {starter.icon ? (
          <img
            className="h-5 w-5 rounded-md"
            src={
              starter.icon?.startsWith('/public')
                ? apiClient.buildEndpoint(starter.icon)
                : starter.icon
            }
            alt={starter.label}
          />
        ) : null}
        <p className="text-[13px] font-semibold leading-5 text-foreground transition-colors duration-150 group-hover:text-primary">
          {starter.label}
        </p>
      </div>
    </Button>
  );
}
